<template>
  <div class="parent-tenant-container">
    <h2 class="page-title">Parent Tenant</h2>
    <p class="page-description">
      This functionality provides a way to update the parent of the current
      company. Updating a parent tenant is a critical action that breaks
      functionality and it is not easily undone. Branch Management app must be
      installed on the master (or parent) account before a Parent UUID can be
      entered.
    </p>
    <ts-board>
      <div v-if="parentId" class="has-parent-content">
        <p class="tenant-id">Tenant ID: {{ parentId }}</p>
        <div data-ts="CompanyCard" id="mycard"></div>
        <ts-help-text
          v-if="$runtime.permissions.can_remove_update_parent_tenant"
          title="Important"
          :messages="[
            'By removing the parent, this branch will become it\'s own master. This can potentially ruin your day!',
          ]"
          type="warning"
        />
      </div>
      <div v-else class="no-parent-content">
        <p>This company has no parent tenant.</p>
        <ts-button
          v-if="$runtime.permissions.can_create_parent_tenant"
          type="primary"
          @click="changeParentModalVisible = true"
          data-testid="addParentTenantBtn"
        >
          Add Parent Tenant
        </ts-button>
      </div>
    </ts-board>
    <footer v-if="parentId">
      <ts-button
        v-if="$runtime.permissions.can_remove_update_parent_tenant"
        type="danger"
        data-testid="openRemoveParentModalBtn"
        @click="removeParentModalVisible = true"
      >
        Remove
      </ts-button>
      <ts-button
        v-if="$runtime.permissions.can_remove_update_parent_tenant"
        type="primary"
        @click="changeParentModalVisible = true"
      >
        Change Parent Tenant
      </ts-button>
    </footer>
    <portal to="asides">
      <ParentTenantModal
        :visible="changeParentModalVisible"
        :tenant-id="tenant.CompanyAccountId"
        @change-tenant-parent="parentChangeHandler"
        @close="changeParentModalVisible = false"
      />
      <ts-confirmation-prompt
        :data-visible="removeParentModalVisible || null"
        data-title="Confirmation required"
        text="To remove the parent tenant for this branch, please type the ID in the box below to confirm your action."
        :data-header="`Remove Parent Tenant: ${parentId}`"
        text-field-label="Tenant ID"
        help-text-title="Important"
        :text-to-match="parentId"
        help-text-messages='["By removing the parent tenant, you will make this branch the master branch."]'
        translations='{"acceptButton": "Remove Parent Tenant"}'
        @accept="removeParent"
        @close="removeParentModalVisible = false"
        data-testid="removeParentModal"
      ></ts-confirmation-prompt>
    </portal>
  </div>
</template>

<script>
import '@tradeshift/elements.board';
import '@tradeshift/elements.button';
import '@tradeshift/elements.confirmation-prompt';
import '@tradeshift/elements.help-text';
import '@tradeshift/elements.typography';
import '@tradeshift/elements/src/vars.css';
import axios from 'axios';
import { notify, blockingLoader } from '../../../utils';
import ParentTenantModal from '../Modals/ParentTenantModal';

export default {
  components: { ParentTenantModal },
  inject: {
    $runtime: {
      default: {
        permissions: {},
      },
    },
    providedData: {
      default: {
        tenant: {},
      },
    },
  },
  data() {
    return {
      tenant: this.providedData.tenant,
      parent: null,
      parentId: this.providedData.tenant?.parent?.Id,
      removeParentModalVisible: false,
      changeParentModalVisible: false,
    };
  },
  async mounted() {
    await this.updateParentInfo();
  },
  methods: {
    async removeParent() {
      blockingLoader.show();
      this.removeParentModalVisible = false;
      axios
        .delete(`/support/tenant/${this.tenant.CompanyAccountId}/parent`)
        .then(() => {
          this.parentId = null;
          this.parent = {};
          notify.succeeded('Parent tenant was successfully removed.');
          axios
            .get(`/support/tenant/${this.tenant.CompanyAccountId}/access`)
            .catch(() => {
              const tenantId = this.tenant.CompanyAccountId;
              notify.info('You lost access request again', {
                onclose() {
                  window.location.href = `/support/tenant/${tenantId}`;
                },
              });
            });
        })
        .catch((error) => notify.failed(error, 'Could not update the parent'))
        .finally(() => blockingLoader.hide());
    },
    async updateParentInfo() {
      if (this.parentId) {
        const companyDetailResp = await axios.get(
          `/info/company/${this.parentId}`
        );
        this.parent = companyDetailResp.data;
        this.renderCompanyCard();
      }
    },
    renderCompanyCard() {
      ts.ui.get('#mycard', (card) => {
        card.render({
          id: this.parentId,
          data: {
            name: this.parent?.CompanyName,
            size: this.parent?.Size || this.parent?.CompanySize,
            location: this.parent?.Country,
            industry: this.parent?.Industry || this.parent?.CompanyIndustry,
            logo: this.parent?.LogoURL || '/images/company_profile.svg',
          },
        });
      });
    },
    parentChangeHandler(eventData) {
      this.parentId = eventData.CompanyAccountId;
      this.parent = {
        ...eventData,
        membersCount: undefined, // FIXME: Current way of getting the count from members list is not working in some cases because of 403
      };
      this.changeParentModalVisible = false;
      this.$nextTick(() => {
        this.renderCompanyCard();
      });
    },
  },
};
</script>

<style>
.ts-card.ts-default,
.ts-companycard.ts-details {
  max-width: 100%;
}
</style>

<style scoped>
.parent-tenant-container {
  max-width: 1000px;
  margin: auto 15%;
}

.has-parent-content {
  padding: var(--ts-unit);
}

.no-parent-content {
  text-align: center;
  padding: var(--ts-unit-triple);
  font-weight: var(--ts-fontweight-semibold);
}

ts-board {
  display: flex;
  flex-direction: column;
}

footer {
  width: 100%;
  background-color: var(--ts-color-gray-lightest);
  box-shadow: var(--ts-board-shadow);
  padding: var(--ts-unit-half) var(--ts-unit);
  text-align: right;
}

footer ts-button {
  margin-left: var(--ts-unit-half);
}

.page-title {
  font-weight: var(--ts-fontweight-bold);
}

.page-description {
  font-size: var(--ts-fontsize);
  color: var(--ts-color-slate-lightest);
  margin-bottom: var(--ts-unit-plus);
}

.tenant-id {
  font-weight: var(--ts-fontweight-semibold);
}
</style>
